import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Badge, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../config';
import './SkillManagement.scss';

const NIVEAU_OPTIONS = ['Anfänger', 'Fortgeschritten', 'Profi', 'Experte'];

export default function SkillManagement() {
  const { token } = useAuth();

  const [allSkills, setAllSkills] = useState([]);
  const [mySkills, setMySkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [selectedSkillId, setSelectedSkillId] = useState('');
  const [selectedNiveau, setSelectedNiveau] = useState('Anfänger');
  const [stundenlohn, setStundenlohn] = useState('');
  const [adding, setAdding] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [skillRes, myRes] = await Promise.all([
        fetch(`${getBackendUrl()}/skill`, { headers }),
        fetch(`${getBackendUrl()}/user/me/skills`, { headers })
      ]);

      if (!skillRes.ok) throw new Error('Globale Skills konnten nicht geladen werden.');
      if (!myRes.ok) throw new Error('Eigene Skills konnten nicht geladen werden.');

      const skillData = await skillRes.json();
      const myData = await myRes.json();

      setAllSkills(skillData);
      setMySkills(myData);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddSkill = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedSkillId) {
      setError('Bitte wählen Sie einen Skill aus.');
      return;
    }

    const lohn = parseFloat(stundenlohn);
    if (isNaN(lohn) || lohn < 0) {
      setError('Bitte geben Sie einen gültigen Stundenlohn ein.');
      return;
    }

    setAdding(true);
    try {
      const res = await fetch(`${getBackendUrl()}/user/me/skills`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          skillId: parseInt(selectedSkillId),
          niveau: selectedNiveau,
          stundenlohn: lohn
        })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Skill konnte nicht hinzugefügt werden.');
      }

      setSuccess('Skill erfolgreich hinzugefügt.');
      setSelectedSkillId('');
      setSelectedNiveau('Anfänger');
      setStundenlohn('');
      await fetchData();
    } catch (e) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveSkill = async (skillId) => {
    if (!window.confirm('Möchten Sie diesen Skill wirklich entfernen?')) return;
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${getBackendUrl()}/user/me/skills/${skillId}`, {
        method: 'DELETE',
        headers
      });

      if (!res.ok) throw new Error('Skill konnte nicht entfernt werden.');

      setSuccess('Skill entfernt.');
      await fetchData();
    } catch (e) {
      setError(e.message);
    }
  };

  const availableSkills = allSkills.filter(
    skill => !mySkills.some(ms => ms.id === skill.id) && skill.name && !skill.name.includes('-deleted')
  );

  if (loading) {
    return (
      <Card className="settings-card skill-card mb-4">
        <Card.Header>Meine Skills</Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" /> Skills werden geladen...
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="settings-card skill-card mb-4">
      <Card.Header>Meine Skills</Card.Header>
      <Card.Body>
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        {mySkills.length > 0 ? (
          <div className="skill-list mb-4">
            {mySkills.map(skill => (
              <div key={skill.id} className="skill-item d-flex justify-content-between align-items-center">
                <div className="skill-info">
                  <span className="skill-name">{skill.name}</span>
                  <Badge className="skill-badge">{skill.niveau}</Badge>
                  <span className="skill-price">{parseFloat(skill.stundenlohn).toFixed(2)} €/h</span>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="skill-remove-btn"
                  onClick={() => handleRemoveSkill(skill.id)}
                >
                  Entfernen
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted mb-4">Sie haben noch keine Skills hinzugefügt.</p>
        )}

        <Form onSubmit={handleAddSkill} className="add-skill-form">
          <h6 className="form-section-title">Neuen Skill hinzufügen</h6>

          {availableSkills.length === 0 ? (
            <p className="text-muted">
              {allSkills.length === 0
                ? 'Es sind aktuell noch keine Skills im System vorhanden.'
                : 'Sie haben bereits alle verfügbaren Skills hinzugefügt.'}
            </p>
          ) : (
            <Row className="g-3">
              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>Fach / Thema</Form.Label>
                  <Form.Select
                    value={selectedSkillId}
                    onChange={(e) => setSelectedSkillId(e.target.value)}
                  >
                    <option value="">Bitte wählen...</option>
                    {availableSkills.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label>Dein Niveau</Form.Label>
                  <Form.Select
                    value={selectedNiveau}
                    onChange={(e) => setSelectedNiveau(e.target.value)}
                  >
                    {NIVEAU_OPTIONS.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group>
                  <Form.Label>Stundenlohn (€)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.50"
                    placeholder="z.B. 15.00"
                    value={stundenlohn}
                    onChange={(e) => setStundenlohn(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={2} className="d-flex align-items-end">
                <Button type="submit" className="form-button w-100" disabled={adding}>
                  {adding ? <Spinner size="sm" /> : 'Hinzufügen'}
                </Button>
              </Col>
            </Row>
          )}
        </Form>
      </Card.Body>
    </Card>
  );
}
